import { useEffect, useState } from 'react';

import noop from 'lodash-es/noop';
import { useDebounceCallback, useEventCallback } from 'usehooks-ts';

/** The shape useController hands back; an unset field value reads as an empty input. */
type ControlledField = {
  value?: string;
  onChange: (value: string) => void;
};

type Params = {
  field: ControlledField;
  delayMs?: number;
};

type DebouncedFieldValue = {
  value: string;
  /** A keystroke has not reached the form yet, so any verdict on its value describes what it replaced */
  isPending: boolean;
  onChange: (value: string) => void;
  /** Writes a pending keystroke through at once — before blur validation and before a native submit */
  flush: VoidFunction;
};

/**
 * Keeps keystrokes local until typing settles, so a field can drive a lookup without spending a
 * request per character. Without `delayMs` the value reaches the form as it always did.
 */
export const useDebouncedFieldValue = ({ field, delayMs }: Params): DebouncedFieldValue => {
  const committed = field.value ?? '';
  const [draft, setDraft] = useState<string>();

  // Stable by contract: useDebounceCallback rebuilds its timer whenever this identity changes,
  // which would hand every keystroke a timer of its own and debounce nothing.
  const write = useEventCallback((value: string) => {
    setDraft(undefined);
    field.onChange(value);
  });
  const writeLater = useDebounceCallback(write, delayMs);

  // A write from elsewhere — a reset, a prefill landing late — replaces whatever was being typed
  // biome-ignore lint/correctness/useExhaustiveDependencies: committed is the trigger, not a read
  useEffect(() => {
    writeLater.cancel();
    setDraft(undefined);
  }, [committed, writeLater]);

  if (!delayMs) {
    return { value: committed, isPending: false, onChange: field.onChange, flush: noop };
  }

  return {
    value: draft ?? committed,
    isPending: draft !== undefined,
    onChange: (value) => {
      setDraft(value);
      writeLater(value);
    },
    flush: writeLater.flush,
  };
};
